//standard library imports
import { useEffect, useState, useContext } from 'react';
//External library imports
import { get as lodashGet, set as lodashSet, keys as lodashKeys, min as lodashMin, max as lodashMax } from 'lodash';
import { toast } from 'react-toastify';
import moment from 'moment';
import { UilCopy, UilInfoCircle, UilImport, UilBolt, UilTrash, UilCapture, UilSync, UilKeyboardShow, UilBan, UilNotes, UilBoltSlash } from '@iconscout/react-unicons';
import { MenuItem, Menu, MenuDivider } from '@szhsin/react-menu';
//Internal module imports
import { useLoader, useQueryParamWithDefault } from '../../../../hooks';
import Typography from '../../../../components/Typography/Typography';
import Table from '../../../../components/Table/Table';
import ModalComponent from '../../../../components/ModalComponent/ModalComponent';
import DatePicker from '../../../../components/Inputs/DatePicker/DatePicker';
import CustomTooltip from '../../../../components/CustomToolTip/CustomTooltip';
import { DropdownComponent } from '../../../../components/Inputs/Input';
import FilterBarClasses from '../../../../styles/Filterbar.module.css';
import { AdminService } from '../../../../services/BiddingService/CommonServices/AdminService';
import { CustomerService } from '../../../../services/BiddingService/CommonServices/CustomerService';
import { RegulationService } from '../../../../services/BiddingService/CommonServices/RegulationService';
import { momentTimeFormater } from '../../../../utils/timeHelper';
import bidClasses from '../Bidding.module.css';
import { RootBiddingService } from '../../../../services/BiddingService/Root/BiddingService';
import IconComponent from '../../../../components/Icon/Icon';
import BiddingInfo from '../ModalComponent/BidInfo';
import DeleteModal from '../../../../components/DeleteModal/DeleteModal';
import CreateBid from '../ModalComponent/CreateBid';
import ResultId from '../ModalComponent/ResultId';
import CreateSecondaryBid from '../ModalComponent/CreateSecondaryBid';
import { DownloadAsExcel } from '../../../../utils/downloadAsExcel';
import { NotesLogsIndex } from '../ModalComponent/Note';
import FetchResult from '../ModalComponent/FetchResult';
import { Panic } from '../ModalComponent/Panic';
import { OverallCapacity } from '../ModalComponent/OverallCapacity';
import CreateBulkBids from '../ModalComponent/CreateBulkBids';
import PostBid from '../ModalComponent/PostBid';
import { PostResouceSchedule } from '../ModalComponent/PostResouceSchedule';
import { AdminBiddingService } from '../../../../services/BiddingService/Admin/BiddingService';
import { useUserRole } from '../../../../hooks/use-user-role.hook';
import DateRangePicker from '../../../../components/Inputs/DateRangePicker/DateRangePicker';
import { Capitalize } from '../../../../utils/stringHelper';
import { dkCurrencyFormat } from '../../../../utils/currencyHelper';
import { UpdatePostedEnergyBid } from '../ModalComponent/UpdatePostedEnergyBid';
import { checkPartnerBidAccess } from '../../../../utils/permissionHelper';
import { AuthContext } from '../../../../context/AuthContext';
import { DateParam, StringParam } from 'use-query-params';

const DEFAULT_DROPDOWN_OPTION = {
    label: 'All',
    value: 'all',
};

const getDropdownOption = (data = [], isDefaultOption = true, labelKey = 'name', valueKey = '_id') => {
    const temp = isDefaultOption ? [DEFAULT_DROPDOWN_OPTION] : [];
    data.map((el) => {
        temp.push({ label: lodashGet(el, labelKey), value: lodashGet(el, valueKey) });
    });
    return temp;
};

const FILTER_OPTIONS = [
    { label: 'All Bids', value: 'all' },
    { label: 'No Bids', value: 'noBids' },
];

const HEADERS_KEYS = {
    masterBid: {
        afrr: {
            headers: [
                'Customer Name',
                'License',
                'Instance',
                'Avg Capacity',
                'Customer CM/EAM (€)',
                'Partner CM/EAM (€)',
                'SC CM/EAM (€)',
                'Enablers CM/EAM (€)',
                'Revenue CM/EAM (€)',
                'Status CM/EAM',
                'Action',
            ],
            keys: [
                'customerName',
                'license',
                'instance',
                'averageCapacity',
                'customerRevenue',
                'partnerRevenue',
                'scRevenue',
                'enablersRevenue',
                'totalRevenue',
                'status',
                'action',
            ],
        },
        fcr: {
            headers: [
                'Customer Name',
                'License',
                'Instance',
                'Avg Capacity',
                'Customer (€)',
                'Partner (€)',
                'SC (€)',
                'Enablers (€)',
                'Revenue (€)',
                'Status(Primary/Secondary)',
                'Action',
            ],
            keys: [
                'customerName',
                'license',
                'instance',
                'averageCapacity',
                'customerRevenue',
                'partnerRevenue',
                'scRevenue',
                'enablersRevenue',
                'totalRevenue',
                'status',
                'action',
            ],
        },
    },
    indivisualMarket: {
        afrr: {
            headers: ['Date', 'Avg Capacity', 'Customer CM/EAM (€)', 'Partner CM/EAM (€)', 'SC CM/EAM (€)', 'Enablers CM/EAM (€)', 'Revenue CM/EAM (€)', 'Status CM/EAM', 'Action'],
            keys: ['cetDate', 'averageCapacity', 'customerRevenue', 'partnerRevenue', 'scRevenue', 'enablersRevenue', 'totalRevenue', 'status', 'action'],
        },
        fcr: {
            headers: ['Date', 'Avg Capacity', 'Customer (€)', 'Partner (€)', 'SC (€)', 'Enablers (€)', 'Revenue (€)', 'Status(Primary/Secondary)', 'Action'],
            keys: ['cetDate', 'averageCapacity', 'customerRevenue', 'partnerRevenue', 'scRevenue', 'enablersRevenue', 'totalRevenue', 'status', 'action'],
        },
    },
};

export const MasterBidding = ({ heading, indivisualMarket = { status: false, market: null }, cId }) => {
    const [initialLoad, setInitialLoad] = useState(true);

    const { state } = useContext(AuthContext);
    const { isRootUser, isAdminUser, adminId } = useUserRole();
    const service = isRootUser ? RootBiddingService : AdminBiddingService;
    const [startLoader, stopLoader] = useLoader();

    const [date, setDate] = useQueryParamWithDefault('date', DateParam, indivisualMarket.status ? undefined : moment().toDate(), '', { clearOnUnmount: false });

    const [admins, setAdmins] = useState([isAdminUser ? {} : DEFAULT_DROPDOWN_OPTION]);
    const [selectedAdmin, setSelectedAdmin] = useQueryParamWithDefault('admin', StringParam, isAdminUser ? adminId : DEFAULT_DROPDOWN_OPTION.value, '', {
        clearOnUnmount: false,
    });

    const [customers, setCustomers] = useState(indivisualMarket.status ? [] : [DEFAULT_DROPDOWN_OPTION]);
    const [selectedCustomer, setSelectedCustomer] = useQueryParamWithDefault('customer', StringParam, indivisualMarket.status ? {} : DEFAULT_DROPDOWN_OPTION.value, '', {
        clearOnUnmount: false,
    });

    const [regulations, setRegulations] = useState([]);
    const [selectedRegulation, setSelectedRegulation] = useQueryParamWithDefault('market', StringParam, indivisualMarket.status ? indivisualMarket.market : '', '', {
        clearOnUnmount: false,
    });

    const [selectedFilter, setSelectedFilter] = useQueryParamWithDefault('filter', StringParam, FILTER_OPTIONS[0].value, cId, { clearOnUnmount: true });

    // for indivisual market bids on admin

    const [marketEnabledCustomerLicenseIns, setMarketEnabledCustomerLicenseIns] = useState({});
    const [licenses, setLicenses] = useState([]);
    const [selectedLicense, setSelectedLicense] = useQueryParamWithDefault('license', StringParam, indivisualMarket.status ? '' : undefined, '', {
        clearOnUnmount: false,
    });

    const [instances, setInstances] = useState([]);
    const [selectedInstance, setSelectedInstance] = useQueryParamWithDefault('instance', StringParam, indivisualMarket.status ? '' : undefined, '', {
        clearOnUnmount: false,
    });

    const [startDate, setStartDate] = useQueryParamWithDefault('startDate', DateParam, indivisualMarket.status ? moment().startOf('month').toDate() : undefined, '', {
        clearOnUnmount: false,
    });
    const [endDate, setEndDate] = useQueryParamWithDefault('endDate', DateParam, indivisualMarket.status ? moment().endOf('month').toDate() : undefined, '', {
        clearOnUnmount: false,
    });

    const [agreement, setAgreement] = useState(null);
    const [totalRevenue, setTotalRevenue] = useState({ capacity: 0, energy: 0 });
    const [minMaxCapacity, setMinMaxCapacity] = useState({
        min: 0,
        max: 0,
    });
    const [isCustomersLoaded, setIsCustomerLoaded] = useState(false);

    //-----------//

    const [tableHeadersAndKeys, setTableHeadersAndKeys] = useState({
        headers: [],
        keys: [],
    });
    const [paginationQuery, setPaginationQuery] = useState({
        skip: 0,
        page: 0,
        limit: 15,
    });
    const [bidsData, setBidsData] = useState({
        bids: [],
        totalCount: 0,
    });

    const [modal, setModal] = useState({
        status: false,
        modalId: null,
        data: {},
    });
    const [deleteModal, setDeleteModal] = useState({
        status: false,
        bidId: null,
        type: null,
    });

    // partner with edit permission
    const [editAccess, setEditAccess] = useState(true);

    // for indivisual market bids on admin

    useEffect(() => {
        if (indivisualMarket.status) {
            if (selectedRegulation !== indivisualMarket.market) {
                setSelectedCustomer('');
                setSelectedLicense('');
                setSelectedInstance('');
            }
            setSelectedRegulation(indivisualMarket.market);

            if (state) setEditAccess(checkPartnerBidAccess(state, indivisualMarket.market));
        }
    }, [indivisualMarket.market]);

    useEffect(() => {
        if (indivisualMarket.status && isCustomersLoaded) {
            if (selectedCustomer && lodashKeys(marketEnabledCustomerLicenseIns).length) {
                const customer = lodashGet(marketEnabledCustomerLicenseIns, [selectedCustomer]);
                const licenses = lodashKeys(customer.licenses).map((lId) => ({
                    label: Capitalize(lodashGet(customer.licenses, [lId, 'type'])),
                    value: lId,
                    ins: lodashGet(customer.licenses, [lId, 'instances'], []),
                }));
                setLicenses(licenses);
                if (!selectedLicense || !initialLoad) setSelectedLicense(lodashGet(licenses, [0, 'value']));
            } else {
                setLicenses([]);
                setSelectedLicense('');
            }
        }
    }, [selectedCustomer, isCustomersLoaded]);

    useEffect(() => {
        if (indivisualMarket.status) {
            if (selectedLicense && licenses.length) {
                const [licenseIns] = licenses?.filter((l) => l.value === selectedLicense);
                const instances = licenseIns?.ins?.map((ins) => ({
                    label: ins.name,
                    value: ins._id,
                    energyMarketOnly: !!ins.energyMarketOnly,
                }));
                setInstances(instances);
                if (!selectedInstance || !initialLoad) setSelectedInstance(lodashGet(instances, [0, 'value']));
            } else {
                if (!selectedLicense) {
                    setInstances([]);
                    setSelectedInstance('');
                }
            }
        }
    }, [selectedLicense, licenses]);

    useEffect(() => {
        if (indivisualMarket.status) getMinMaxCapacity();
    }, [bidsData.bids]);

    // ------------- //

    useEffect(() => {
        if (indivisualMarket?.status) {
            if (!startDate) setStartDate(moment().startOf('month').toDate());
            if (!endDate) setEndDate(moment().endOf('month').toDate());
        }
    }, [indivisualMarket?.market]);

    useEffect(() => {
        if (isRootUser && !indivisualMarket.status) getAdmins();
        if (!indivisualMarket.status) getRegulation();
    }, []);

    useEffect(() => {
        if (selectedAdmin && selectedAdmin !== 'all' && !indivisualMarket.status) getCustomers();
        else if (selectedAdmin === 'all') {
            setCustomers([DEFAULT_DROPDOWN_OPTION]);
            setSelectedCustomer(DEFAULT_DROPDOWN_OPTION.value);
        }
    }, [selectedAdmin]);

    useEffect(() => {
        const key = !indivisualMarket.status ? 'masterBid' : 'indivisualMarket';
        if (selectedRegulation?.includes('aFRR') || selectedRegulation?.includes('mFRR')) setTableHeadersAndKeys(lodashGet(HEADERS_KEYS, [key, 'afrr']));
        else setTableHeadersAndKeys(lodashGet(HEADERS_KEYS, [key, 'fcr']));

        if (indivisualMarket.status && selectedRegulation) getMarketEnabledCusLicenseIns();
    }, [selectedRegulation]);

    useEffect(() => {
        if ((selectedCustomer && selectedRegulation && !indivisualMarket.status) || (indivisualMarket.status && selectedInstance && selectedCustomer)) getBids();
    }, [paginationQuery.skip, selectedAdmin, selectedCustomer, selectedRegulation, selectedInstance]);

    const getRegulation = () => {
        const params = { date: momentTimeFormater(date).startOf('day').format() };
        RegulationService.GetRegulationSortedOnBids(params, startLoader, handleGetRegulationSuccess, handleError, stopLoader);
    };

    const handleGetRegulationSuccess = ({ data }) => {
        const options = getDropdownOption(data.data, false, 'name', 'name');
        if (!selectedRegulation) setSelectedRegulation(options[0].value);
        setRegulations(options);
    };

    const getAdmins = () => {
        AdminService.GetAdmins(startLoader, handleGetAdminSuccess, handleError, stopLoader);
    };

    const handleGetAdminSuccess = ({ data }) => {
        const options = getDropdownOption(data.data);
        if (!selectedAdmin) setSelectedAdmin(options[0].value);
        setAdmins(options);
    };

    const getCustomers = () => {
        const params = { adminId: selectedAdmin };
        CustomerService.GetCustomers(params, startLoader, handleGetCustomerSuccess, handleError, stopLoader);
    };

    const handleGetCustomerSuccess = ({ data }) => {
        const options = getDropdownOption(data.data);
        if (!selectedCustomer || !initialLoad) setSelectedCustomer(options[0].value);
        setCustomers(options);
    };

    const getBids = () => {
        if (!indivisualMarket.status) {
            const params = {
                limit: paginationQuery.limit,
                skip: paginationQuery.skip,
                market: selectedRegulation,
                bidDate: moment(date).format('YYYY-MM-DD'),
                adminId: selectedAdmin,
                customerId: selectedCustomer,
                filter: selectedFilter,
            };
            service.GetMasterBids(params, startLoader, handleBidsSuccess, handleError, stopLoader);
        } else {
            const params = {
                limit: paginationQuery.limit,
                skip: paginationQuery.skip,
                market: selectedRegulation,
                startDate: moment(startDate).format('YYYY-MM-DD'),
                endDate: moment(endDate).format('YYYY-MM-DD'),
                customerId: selectedCustomer,
                instanceId: selectedInstance,
            };
            AdminBiddingService.GetBids(params, startLoader, handleBidsSuccess, handleError, stopLoader);
        }
    };

    const handleNull = (value) => (value || value === 0 ? (!isNaN(value) ? dkCurrencyFormat(value) : value) : '--');

    const handleExport = (bid) => {
        if (selectedRegulation?.includes('FCR-D')) {
            let totalPrimaryPrice = 0;
            let totalSecondaryPrice = 0;
            let totalPrice = 0;

            const data = bid.capacity.map((cap, index) => {
                const primarySettlementPrice = lodashGet(bid, ['primary', 'provider', 'bidStatus', index, 'price'], 0);
                const secondarySettlementPrice = lodashGet(bid, ['secondary', 'provider', 'bidStatus', index, 'price'], 0);
                const totalSettlementPrice = lodashGet(bid, ['economics', 'hourlyData', index, 'price'], 0);

                totalPrimaryPrice += primarySettlementPrice;
                totalSecondaryPrice += secondarySettlementPrice;
                totalPrice += totalSettlementPrice;

                return {
                    hour: index,
                    primaryCapacity: lodashGet(bid, ['primary', 'capacity', index], '--'),
                    primaryPrice: lodashGet(bid, ['primary', 'price', index], '--'),
                    primarySettlement: primarySettlementPrice,
                    secondaryCapacity: lodashGet(bid, ['secondary', 'capacity', index], '--'),
                    secondaryPrice: lodashGet(bid, ['secondary', 'price', index], '--'),
                    secondarySettlement: secondarySettlementPrice,
                    hourcost: totalSettlementPrice,
                };
            });

            data.push({
                hour: 'Total',
                primaryCapacity: '',
                primaryPrice: '',
                primarySettlement: totalPrimaryPrice,
                secondaryCapacity: '',
                secondaryPrice: '',
                secondarySettlement: totalSecondaryPrice,
                hourcost: totalPrice,
            });

            DownloadAsExcel(data, `${selectedRegulation}_${momentTimeFormater(bid?.date).format('YYYY-MM-DD')}`, [
                'Hour',
                'Capacity (MW) (Primary)',
                'Price (€) (Primary)',
                'Settlement (€) (Primary)',
                'Capacity (MW) (Secondary)',
                'Pricem (€) (Secondary)',
                'Selttlement (€) (Secondary)',
                'Settlement (€) ( Primary & Secondary )',
            ]);
        } else {
            const data = lodashGet(bid, ['primary', 'capacity'], []).map((cap, index) => ({
                hour: index,
                capacity: cap,
                price: lodashGet(bid, ['primary', 'price', index], '--'),
                hourcost: lodashGet(bid, ['bidSattlement', index, 'price'], '--'),
            }));
            DownloadAsExcel(data, `${selectedRegulation}_${momentTimeFormater(bid?.date).format('YYYY-MM-DD')}`, ['Hour', 'Capacity', 'Price', 'Settlement']);
        }
    };

    const handleBidsSuccess = ({ data }) => {
        const enablers = lodashGet(data, ['data', 'enablers'], []);
        const processedData = lodashGet(data, ['data', 'bids'], []).map((item) => {
            if (indivisualMarket.status) item.cetDate = momentTimeFormater(item.date).format('YYYY-MM-DD');
            if (!item.isBidPresent) item.style = { background: '#FFE3E3', color: '#E25657' };
            else if (!lodashGet(item, ['bid', 'settlement'], []).length && !lodashGet(item, ['bid', 'energySettlement'], []).length)
                item.style = { background: '#E6E8FA', color: '#4C5AD9' };

            item.averageCapacity = handleNull(item.averageCapacity);
            item.status = `${handleNull(item.primaryStatus)}/${handleNull(item.secondaryStatus)}`;

            if (selectedRegulation.includes('aFRR') || selectedRegulation.includes('mFRR')) {
                const isInactive = lodashGet(item, ['bid', 'primary', 'energyRequestBidStatus'], []).some((el) => el === 'inactive');
                item.customerRevenue = `${handleNull(item.customerRevenue)} / ${handleNull(item.customerRevenueEnergy)}`;
                item.partnerRevenue = `${handleNull(item.partnerRevenue)} / ${handleNull(item.partnerRevenueEnergy)}`;
                item.scRevenue = `${handleNull(item.scRevenue)} / ${handleNull(item.scRevenueEnergy)}`;
                item.totalRevenue = `${handleNull(item.totalRevenue)} / ${handleNull(item.totalRevenueEnergy)}`;
                if (indivisualMarket.status)
                    item.enablersRevenue = (
                        <span className={bidClasses.Flex} style={{ justifyContent: 'center', gap: '0.2vw' }}>
                            {handleNull(item.enablersRevenue) !== '--' ? getEnablerRevenue(item.allEnablersRevenue, enablers) : '--'}
                            {'/'}
                            {handleNull(item.enablersRevenueEnergy) !== '--' ? getEnablerRevenue(item.allEnablersRevenueEnergy, enablers) : '--'}
                        </span>
                    );
                else item.enablersRevenue = `${handleNull(item.enablersRevenue)} / ${handleNull(item.enablersRevenueEnergy)}`;
                if (isInactive) item.style = { background: '#fef6e5', color: '#faa500' };
            } else {
                item.customerRevenue = handleNull(item.customerRevenue);
                item.partnerRevenue = handleNull(item.partnerRevenue);
                item.scRevenue = handleNull(item.scRevenue);
                item.totalRevenue = handleNull(item.totalRevenue);
                if (indivisualMarket.status) item.enablersRevenue = item.enablersRevenue !== '--' ? getEnablerRevenue(item.allEnablersRevenue, enablers) : '--';
                else item.enablersRevenue = handleNull(item.enablersRevenue);
            }

            const isPrimaryDeleteDisabled = lodashGet(item, ['bid', 'primary', 'provider', 'bidStatus'], []).length
                ? true
                : (selectedRegulation?.includes('aFRR') || selectedRegulation?.includes('mFRR')) &&
                  lodashGet(item, ['bid', 'primary', 'energyRequestBidStatus'], []).some((el) => el === 'active');

            item.action = (
                <div className={bidClasses.ActionContainer}>
                    {!item.isBidPresent && editAccess ? (
                        <div
                            onClick={() => {
                                if (item.isAgreement) {
                                    setModal({
                                        status: true,
                                        modalId: 'createBid',
                                        data: {
                                            ...(indivisualMarket.status
                                                ? { instance: instances?.filter((el) => el.value === selectedInstance)?.[0] }
                                                : {
                                                      instance: {
                                                          label: item.instance,
                                                          value: item.instanceId,
                                                          energyMarketOnly: !!item.energyMarketOnly,
                                                      },
                                                  }),
                                            customerId: item.customerId,
                                            market: selectedRegulation,
                                            bidDate: date,
                                            ...(selectedRegulation?.includes('aFRR') || selectedRegulation?.includes('mFRR')
                                                ? {
                                                      cost: item?.cost,
                                                  }
                                                : {}),
                                        },
                                    });
                                } else toast.error('No Agreement Found');
                            }}
                        >
                            <IconComponent icon={UilCopy} tooltip={'Create Bid'} />
                        </div>
                    ) : (
                        <>
                            <div
                                onClick={() => {
                                    setModal({
                                        status: true,
                                        modalId: 'bidInfo',
                                        data: {
                                            capacity: {
                                                overall: lodashGet(item, ['bid', 'capacity'], []),
                                                primary: lodashGet(item, ['bid', 'primary', 'capacity'], []),
                                                secondary: lodashGet(item, ['bid', 'secondary', 'capacity'], []),
                                                panicHours: lodashGet(item, ['bid', 'panic', 'hours'], []),
                                            },
                                            price: {
                                                overall: lodashGet(item, ['bid', 'price'], []),
                                                primary: lodashGet(item, ['bid', 'primary', 'price'], []),
                                                secondary: lodashGet(item, ['bid', 'secondary', 'price'], []),
                                                ...(lodashGet(item, ['bid', 'market'], '').includes('aFRR') || lodashGet(item, ['bid', 'market'], '').includes('mFRR')
                                                    ? {
                                                          regulationPrice: lodashGet(item, ['bid', 'primary', 'regulationPrice'], []),
                                                          energyRequestBidStatus: lodashGet(item, ['bid', 'primary', 'energyRequestBidStatus'], []),
                                                          energyStatusEffectiveFrom: lodashGet(item, ['bid', 'primary', 'energyStatusEffectiveFrom'], []),
                                                      }
                                                    : {}),
                                            },
                                            settlement: {
                                                bidStatus: lodashGet(item, ['bid', 'settlement'], []),
                                                energyStatus: lodashGet(item, ['bid', 'energySettlement'], []),
                                            },
                                            bidDate: lodashGet(item, ['bid', 'date']),
                                        },
                                    });
                                }}
                            >
                                <IconComponent icon={UilInfoCircle} tooltip={'Bid Info'} />
                            </div>
                            <div
                                onClick={() => {
                                    handleExport(item.bid);
                                }}
                            >
                                <IconComponent icon={UilImport} tooltip={'Download'} />
                            </div>

                            {editAccess && (
                                <>
                                    {lodashGet(item, ['bid', 'bidType']) === 'normal' ? (
                                        <>
                                            {(selectedRegulation?.includes('FCR-D') || selectedRegulation?.includes('FCR-N')) &&
                                                lodashGet(item, ['bid', 'secondary', 'isDeleted']) && (
                                                    <div
                                                        onClick={() => {
                                                            setModal({
                                                                status: true,
                                                                modalId: 'createSecondaryBid',
                                                                data: item.bid,
                                                            });
                                                        }}
                                                    >
                                                        <IconComponent icon={UilSync} tooltip={'Secondary Bid'} />
                                                    </div>
                                                )}

                                            {!selectedRegulation?.includes('aFRR') && !selectedRegulation?.includes('mFRR') && (
                                                <>
                                                    <div
                                                        onClick={() => {
                                                            setModal({
                                                                status: true,
                                                                modalId: 'fetchResult',
                                                                data: item.bid,
                                                            });
                                                        }}
                                                    >
                                                        <IconComponent icon={UilCapture} tooltip={'Fetch Result'} />
                                                    </div>

                                                    <div
                                                        onClick={() => {
                                                            setModal({
                                                                status: true,
                                                                modalId: 'resultId',
                                                                data: item.bid,
                                                            });
                                                        }}
                                                    >
                                                        <IconComponent icon={UilKeyboardShow} tooltip={'Result ID'} />
                                                    </div>
                                                </>
                                            )}

                                            {(selectedRegulation?.includes('aFRR') || selectedRegulation?.includes('mFRR')) && (
                                                <div
                                                    onClick={() => {
                                                        setModal({
                                                            status: true,
                                                            modalId: 'updateEnergyBid',
                                                            data: item.bid,
                                                        });
                                                    }}
                                                >
                                                    <IconComponent icon={UilBoltSlash} tooltip={'Update energy bid'} />
                                                </div>
                                            )}

                                            <Menu
                                                menuButton={
                                                    <div>
                                                        <IconComponent icon={UilTrash} tooltip={'Delete'} />
                                                    </div>
                                                }
                                                direction={'left'}
                                                arrow={true}
                                            >
                                                {lodashGet(item, ['bid', 'primary', 'capacity'], []).length ? (
                                                    <>
                                                        <MenuItem
                                                            disabled={isPrimaryDeleteDisabled}
                                                            onClick={() => {
                                                                setDeleteModal({ status: true, bidId: item.bid._id, type: 'primary' });
                                                            }}
                                                        >
                                                            Delete Primary Bid {lodashGet(item, ['bid', 'primary', 'provider', 'bidStatus'], []).length ? '(Result Fetched)' : ''}
                                                        </MenuItem>
                                                        {lodashGet(item, ['bid', 'secondary', 'capacity'], []).length ? <MenuDivider /> : ''}
                                                    </>
                                                ) : (
                                                    ''
                                                )}

                                                {lodashGet(item, ['bid', 'secondary', 'capacity'], []).length ? (
                                                    <MenuItem
                                                        disabled={lodashGet(item, ['bid', 'secondary', 'provider', 'bidStatus'], []).length ? true : false}
                                                        onClick={() => {
                                                            setDeleteModal({ status: true, bidId: item.bid._id, type: 'secondary' });
                                                        }}
                                                    >
                                                        <div>
                                                            Delete Secondary Bid{' '}
                                                            {lodashGet(item, ['bid', 'secondary', 'provider', 'bidStatus'], []).length ? '(Result Fetched)' : ''}
                                                        </div>
                                                    </MenuItem>
                                                ) : (
                                                    ''
                                                )}
                                            </Menu>

                                            {lodashGet(item, ['bid', 'settlement'], []).length || lodashGet(item, ['bid', 'energySettlement'], []).length ? (
                                                <div
                                                    onClick={() => {
                                                        setModal({
                                                            status: true,
                                                            modalId: 'panic',
                                                            data: item.bid,
                                                        });
                                                    }}
                                                >
                                                    <IconComponent icon={UilBan} tooltip={'Panic'} />
                                                </div>
                                            ) : (
                                                ''
                                            )}
                                            <div
                                                onClick={() => {
                                                    setModal({
                                                        status: true,
                                                        modalId: 'note',
                                                        data: item.bid,
                                                    });
                                                }}
                                            >
                                                <IconComponent icon={UilNotes} tooltip={'Bid Note'} />
                                            </div>
                                        </>
                                    ) : (
                                        <></>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>
            );

            return item;
        });
        if (indivisualMarket.status) {
            setAgreement(lodashGet(data, ['data', 'aggrement'], null));
            setTotalRevenue(lodashGet(data, ['data', 'totalRevenue']));
        }
        setBidsData({ bids: processedData, totalCount: lodashGet(data, ['data', 'totalCount'], 0) });
        setInitialLoad(false);
    };

    const handleDelete = () => {
        service.DeleteBid(deleteModal.bidId, { type: deleteModal.type }, startLoader, handleDeleteSuccess, handleError, stopLoader);
    };

    const handleDeleteSuccess = (res) => {
        getBids();
        toast.success('Bid Deleted Successfully');
    };

    const handleError = (err) => {
        toast.error(lodashGet(err, ['response', 'data', 'message']));
    };

    const handlePageChange = (pageNo) => {
        setPaginationQuery({ ...paginationQuery, skip: paginationQuery.limit * pageNo, page: pageNo });
    };

    const closeModal = () => {
        setModal({ status: false, modalId: null, data: {} });
    };

    const getMarketEnabledCusLicenseIns = () => {
        AdminBiddingService.GetMarketEnabledCusLicenseIns(selectedRegulation, startLoader, getMarketEnabledCusLicenseInsSuccess, handleError, stopLoader);
    };

    const getMarketEnabledCusLicenseInsSuccess = ({ data }) => {
        data = lodashGet(data, ['data'], {});
        setMarketEnabledCustomerLicenseIns(data);
        const customers = lodashKeys(data);
        if (customers.length) {
            const customerOptions = customers.map((cusId) => ({
                label: lodashGet(data, [cusId, 'name']),
                value: cusId,
                biddingMarkets: lodashGet(data, [cusId, 'biddingMarkets']),
            }));
            setCustomers(customerOptions);
            if (!selectedCustomer || !initialLoad) setSelectedCustomer(lodashGet(customerOptions, [0, 'value']));
        } else toast.error('No Instances Enabled');
        setIsCustomerLoaded(true);
    };

    const getMinMaxCapacity = () => {
        const capacity = [];
        bidsData.bids.map((bid) => {
            if (bid.averageCapacity !== '--') capacity.push(bid.averageCapacity);
        });
        setMinMaxCapacity({
            min: lodashMin(capacity) || 0,
            max: lodashMax(capacity) || 0,
        });
    };

    const getEnablerRevenue = (enablersRev = {}, enablersData = []) => {
        let totalRevenue = 0;
        let tooltip = [];

        enablersRev &&
            enablersData.map((e) => {
                for (const key in enablersRev) {
                    if (key == e._id) {
                        totalRevenue = totalRevenue + lodashGet(enablersRev, [key]);
                        tooltip.push(`${e.name} : ${dkCurrencyFormat(lodashGet(enablersRev, [key], 0))}`);
                    }
                }
            });

        return (
            <CustomTooltip
                content={tooltip.map((e) => (
                    <div>{e}</div>
                ))}
            >
                {dkCurrencyFormat(totalRevenue)}
            </CustomTooltip>
        );
    };

    return (
        <div>
            <div className={FilterBarClasses.ParentWrapper} style={{ alignItems: 'center' }}>
                <div className={FilterBarClasses.Left}>
                    {isRootUser && (
                        <div className={FilterBarClasses.Filter}>
                            <label>Admin</label>
                            <div className={bidClasses.DropdownContainer}>
                                <DropdownComponent
                                    defaultValue={{ value: selectedAdmin }}
                                    options={admins}
                                    onChange={(e) => {
                                        setSelectedAdmin(e.value);
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    <div className={FilterBarClasses.Filter}>
                        <label>Customer</label>
                        <div className={bidClasses.DropdownContainer}>
                            <DropdownComponent
                                defaultValue={{ value: selectedCustomer }}
                                options={customers}
                                onChange={(e) => {
                                    if (e.value !== selectedCustomer) {
                                        if (indivisualMarket?.status) {
                                            setSelectedCustomer('');
                                            setSelectedLicense('');
                                            setSelectedInstance('');
                                        }
                                        setSelectedCustomer(e.value);
                                    }
                                }}
                            />
                        </div>
                    </div>

                    {/* market and filter for master bids */}
                    <div className={FilterBarClasses.Filter}>
                        <label>{indivisualMarket.status ? 'License' : 'Market'}</label>
                        <div className={bidClasses.DropdownContainer}>
                            <DropdownComponent
                                defaultValue={indivisualMarket.status ? { value: selectedLicense } : { value: selectedRegulation }}
                                options={indivisualMarket.status ? licenses : regulations}
                                onChange={(e) => {
                                    if (indivisualMarket.status) setSelectedLicense(e.value);
                                    else setSelectedRegulation(e.value);
                                }}
                            />
                        </div>
                    </div>
                    <div className={FilterBarClasses.Filter}>
                        <label> {indivisualMarket.status ? 'Instance' : 'Filter'} </label>
                        <div className={bidClasses.DropdownContainer}>
                            <DropdownComponent
                                defaultValue={indivisualMarket.status ? { value: selectedInstance } : { value: selectedFilter }}
                                options={indivisualMarket.status ? instances : FILTER_OPTIONS}
                                onChange={(e) => {
                                    if (indivisualMarket.status) setSelectedInstance(e.value);
                                    else setSelectedFilter(e.value);
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className={FilterBarClasses.Right} style={{ alignItems: 'center' }}>
                    <div className={FilterBarClasses.Filter}>
                        {indivisualMarket.status ? (
                            <>
                                {startDate && endDate && (
                                    <DateRangePicker
                                        startDate={startDate}
                                        endDate={endDate}
                                        onChange={(date) => {
                                            setStartDate(moment(date.startDate).toDate());
                                            setEndDate(moment(date.endDate).toDate());
                                        }}
                                        showLabels={true}
                                    />
                                )}
                            </>
                        ) : (
                            <>
                                <label>Date</label>
                                <DatePicker
                                    date={date}
                                    setDate={(d) => {
                                        setDate(d);
                                    }}
                                    showArrow={true}
                                    disableFutureDate={false}
                                />
                            </>
                        )}
                    </div>
                    <button className="btn-primary" style={{ width: 'auto' }} onClick={getBids}>
                        Submit
                    </button>
                    <button
                        className="btn-primary"
                        style={{ width: 'auto' }}
                        onClick={() => {
                            if (indivisualMarket.status && !agreement) {
                                toast.error('No Agreement Found');
                            } else {
                                setModal({
                                    status: true,
                                    modalId: indivisualMarket.status ? 'createBid' : 'bulkBid',
                                    data: {
                                        ...(indivisualMarket.status
                                            ? { instance: instances?.filter((el) => el.value === selectedInstance)?.[0] }
                                            : { adminId: selectedAdmin, bidDate: date }),
                                        customerId: selectedCustomer,
                                        market: selectedRegulation,
                                        ...(selectedRegulation?.includes('aFRR') || selectedRegulation?.includes('mFRR')
                                            ? {
                                                  cost: lodashGet(agreement, ['instances', selectedInstance, 'cost'], 0),
                                              }
                                            : {}),
                                    },
                                });
                            }
                        }}
                    >
                        {indivisualMarket.status ? 'Create Bid' : 'Create Bulk Bid'}
                    </button>
                </div>
            </div>

            <div className={bidClasses.MainContainer}>
                <div className={bidClasses.TableHeader}>
                    <div>
                        <Typography content={heading} />
                        <div className={bidClasses.TableCount}>
                            Total Count :
                            <span>
                                <Typography size="14" content={bidsData.totalCount} />
                            </span>
                        </div>

                        {indivisualMarket.status && (
                            <>
                                <div className={bidClasses.TableCount}>
                                    Total Revenue (CM) :
                                    <span>
                                        <Typography size="14" content={totalRevenue.capacity + ' €'} />
                                    </span>
                                </div>
                                {indivisualMarket.market.includes('aFRR') || indivisualMarket.market.includes('mFRR') ? (
                                    <div className={bidClasses.TableCount}>
                                        Total Revenue (EAM) :
                                        <span>
                                            <Typography size="14" content={totalRevenue.energy + ' €'} />
                                        </span>
                                    </div>
                                ) : (
                                    ''
                                )}
                                <div className={bidClasses.TableCount}>
                                    Max Capacity (MW):
                                    <span>
                                        <Typography size="14" content={minMaxCapacity.max} />
                                    </span>
                                </div>

                                <div className={bidClasses.TableCount}>
                                    Min Capacity (MW):
                                    <span>
                                        <Typography size="14" content={minMaxCapacity.min} />
                                    </span>
                                </div>
                            </>
                        )}
                    </div>

                    {!indivisualMarket.status && (
                        <div className={bidClasses.Flex}>
                            <div>
                                <CustomTooltip content={'Overall Capacity'}>
                                    <div
                                        onClick={() => {
                                            setModal({
                                                status: true,
                                                modalId: 'overallCapacity',
                                                data: {
                                                    market: selectedRegulation,
                                                    bidDate: momentTimeFormater(date).format('YYYY-MM-DD'),
                                                    adminId: selectedAdmin,
                                                    customerId: selectedCustomer,
                                                },
                                            });
                                        }}
                                    >
                                        <UilInfoCircle size={'1.2vw'} style={{ color: 'var(--color-primary)' }} />
                                    </div>
                                </CustomTooltip>
                            </div>
                            <div style={{ fontSize: '0.72vw' }}>
                                <CustomTooltip content={'Bid'}>
                                    <Menu
                                        menuButton={
                                            <div>
                                                <UilBolt size={'1.2vw'} style={{ color: 'var(--color-primary)' }} />
                                            </div>
                                        }
                                        direction={'left'}
                                        arrow={true}
                                    >
                                        <MenuItem
                                            onClick={() => {
                                                setModal({ status: true, modalId: 'postBid', data: { type: 'primary', bidDate: date } });
                                            }}
                                        >
                                            <div>Post Primary Bid</div>
                                        </MenuItem>
                                        <MenuDivider />

                                        <MenuItem
                                            onClick={() => {
                                                setModal({ status: true, modalId: 'postBid', data: { type: 'secondary', bidDate: date } });
                                            }}
                                        >
                                            <div>Post Secondary Bid</div>
                                        </MenuItem>
                                        <MenuDivider />

                                        <MenuItem
                                            onClick={() => {
                                                setModal({ status: true, modalId: 'postResourceSchedule', data: null });
                                            }}
                                        >
                                            <div>Post Resource Schedule</div>
                                        </MenuItem>
                                    </Menu>
                                </CustomTooltip>
                            </div>
                        </div>
                    )}
                </div>
                <Table
                    head={tableHeadersAndKeys.headers}
                    keys={tableHeadersAndKeys.keys}
                    data={bidsData.bids}
                    page={paginationQuery.page}
                    Pagination={true}
                    limit={paginationQuery.limit}
                    handlePageChange={handlePageChange}
                    totalCount={bidsData.totalCount}
                />
            </div>
            {modal.status && (
                <ModalComponent isOpen={modal.status} setOpen={() => closeModal()}>
                    {modal.modalId === 'bidInfo' && <BiddingInfo data={modal.data} />}
                    {modal.modalId === 'createBid' && <CreateBid data={modal.data} closeModal={closeModal} getBids={getBids} />}
                    {modal.modalId === 'resultId' && <ResultId bid={modal.data} closeModal={closeModal} getBids={getBids} />}
                    {modal.modalId === 'createSecondaryBid' && <CreateSecondaryBid bid={modal.data} closeModal={closeModal} getBids={getBids} />}
                    {modal.modalId === 'note' && <NotesLogsIndex bid={modal.data} closeModal={closeModal} bidType={'normal'} />}
                    {modal.modalId === 'fetchResult' && <FetchResult bid={modal.data} closeModal={closeModal} />}
                    {modal.modalId === 'panic' && <Panic bid={modal.data} closeModal={closeModal} getBids={getBids} bidType={'normal'} />}
                    {modal.modalId === 'overallCapacity' && <OverallCapacity params={modal.data} />}
                    {modal.modalId === 'bulkBid' && <CreateBulkBids data={modal.data} closeModal={closeModal} getBids={getBids} />}
                    {modal.modalId === 'postBid' && <PostBid data={modal.data} closeModal={closeModal} getBids={getBids} />}
                    {modal.modalId === 'postResourceSchedule' && <PostResouceSchedule closeModal={closeModal} />}
                    {modal.modalId === 'updateEnergyBid' && <UpdatePostedEnergyBid bid={modal.data} closeModal={closeModal} bidType={'normal'} />}
                </ModalComponent>
            )}
            {deleteModal.status && (
                <DeleteModal
                    deletefunction={handleDelete}
                    opendeleteModal={deleteModal.status}
                    setOpenDeleteModal={() => setDeleteModal({ status: false, bidId: null, type: null })}
                />
            )}
        </div>
    );
};
